"use client";

import Image from "next/image";
import { IconCalendarEvent, IconMapPin } from "@tabler/icons-react";
import type { LogoControllerSection } from "@/app/(dashboard)/dashboard/webmaster/logo-controller/logo-controller-defaults";
import { Reveal } from "../../../components/cambodia-food-expo/ui/Reveal";
import { SponsorSection } from "../../../components/cambodia-food-expo/sections/SponsorSection";
import type { EventProgram } from "./types";
import { SectionHeading, SectionLink, UI, useText } from "./shared";

/** Reuses the homepage logo grid (Organized by / Sponsors / Media partners from Logo Controller). */
export function EventSponsors({
  logoSection,
}: {
  logoSection: LogoControllerSection | null;
}) {
  const { text } = useText();
  if (!logoSection?.is_active || logoSection.logos.length === 0) return null;
  return (
    <section
      aria-labelledby="event-sponsors-title"
      className="event-dark bg-[#071b4a] border-t border-white/10 pt-16 md:pt-24"
    >
      <div className="mx-auto w-full max-w-[1280px] px-5 sm:px-6">
        <SectionHeading id="event-sponsors-title" title={text(UI.sponsors)} />
      </div>
      <div className="-mt-16 md:-mt-24">
        <SponsorSection logoSection={logoSection} />
      </div>
    </section>
  );
}

export function EventCTA({
  program,
  homeHref,
}: {
  program: EventProgram;
  homeHref: string;
}) {
  const { text } = useText();
  const background = program.heroImages[1] ?? program.heroImages[0];
  return (
    <section
      aria-labelledby="event-cta-title"
      className="event-dark bg-[#071b4a] px-5 py-16 sm:px-6 md:py-24"
    >
      <Reveal className="relative isolate mx-auto max-w-[1280px] overflow-hidden rounded-[28px] border border-[#f6cf63]/30">
        {background && (
          <Image
            src={background}
            alt=""
            fill
            sizes="(min-width: 1280px) 1280px, 100vw"
            className="-z-10 object-cover"
          />
        )}
        <div className="absolute inset-0 -z-10 bg-[linear-gradient(120deg,rgba(6,24,62,0.96),rgba(6,24,62,0.82)_55%,rgba(191,30,46,0.55))]" />
        <div className="px-6 py-14 text-center md:px-12 md:py-20">
          <SectionHeading id="event-cta-title" title={text(UI.ctaTitle)} />
          <div className="-mt-4 flex flex-col items-center justify-center gap-3 text-white/90 sm:flex-row sm:gap-6">
            <p className="inline-flex items-center gap-2 font-semibold text-[#f6cf63]">
              <IconCalendarEvent className="size-5" aria-hidden="true" />
              {text(program.dateRange)}
            </p>
            <p className="inline-flex items-center gap-2">
              <IconMapPin
                className="size-5 text-[#f6cf63]"
                aria-hidden="true"
              />
              {text(program.venue)}, {text(program.city)}
            </p>
          </div>
          <div className="mt-9 flex flex-col items-center justify-center gap-3 sm:flex-row">
            {/* The site footer carries id="contact" with phone, email and social links. */}
            <SectionLink
              to="contact"
              className="inline-flex w-full items-center justify-center rounded-full bg-[#f6cf63] px-7 py-3 font-bold text-[#041127] transition hover:bg-[#f0bd3a] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white sm:w-auto"
            >
              {text(UI.contactUs)}
            </SectionLink>
            <a
              href={`${homeHref}#about`}
              className="inline-flex w-full items-center justify-center rounded-full border border-white/30 bg-white/10 px-7 py-3 font-semibold text-white backdrop-blur transition hover:border-white/60 hover:bg-white/15 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#f6cf63] sm:w-auto"
            >
              {text(UI.learnMore)}
            </a>
          </div>
        </div>
      </Reveal>
    </section>
  );
}
